import { useState } from "react";
import { PlusIcon, UsersIcon } from "lucide-react";

import { AccountSettingsCompanyUsersTable } from "./AccountSettingsCompanyUsersTable";
import { SettingsInviteUserDialog } from "./SettingsInviteUserDialog";
import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useGetCompanyUsersForSettings } from "@/hooks/queries/useGetCompanyUsersForSettings";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";


export const CompanySettings = ({ className }: { className?: string }) => {
  const currentCompanyId = useCurrentCompanyId();
  const cachedCompanies = useGetCachedCompaniesSummary();
  const [isInviteUserDialogOpen, setIsInviteUserDialogOpen] = useState(false);

  const {
    data: companyUsers = [],
    isError: isCompanyUsersError,
    isPending: isCompanyUsersPending,
  } = useGetCompanyUsersForSettings();

  const user = useGetCurrentUser();
  if (!user) {
    return null;
  }

  const currentCompany = cachedCompanies.find(
    (company) => company.id === currentCompanyId,
  );
  if (!currentCompany) {
    throw new Error("Current company not found in user's companies");
  }

  const render = () => {
    if (isCompanyUsersPending) {
      return <LoadingIndicator variant="medium" />;
    } else if (isCompanyUsersError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading company users. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    }
    return (
      <Column className={className}>
        <Column className="gap-4">
          <Row className="items-center justify-between">
            <Row className="items-center gap-1">
              <UsersIcon className="h-5 w-5 text-slate-700" />
              <Text>
                {companyUsers.length} user
                {companyUsers.length === 1 ? "" : "s"}
              </Text>
            </Row>
            <Button
              className="rounded-full"
              type="button"
              onClick={() => {
                setIsInviteUserDialogOpen(true);
              }}
            >
              <PlusIcon />
              Add user
            </Button>
            <SettingsInviteUserDialog
              company={currentCompany}
              currentUserId={user.id}
              isOpen={isInviteUserDialogOpen}
              setIsOpen={setIsInviteUserDialogOpen}
            />
          </Row>
          <AccountSettingsCompanyUsersTable
            company={currentCompany}
            companyUsers={companyUsers}
            setIsInviteUserDialogOpen={setIsInviteUserDialogOpen}
          />
        </Column>
      </Column>
    );
  };

  return render();
};
